import { HttpClient, HttpContext, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import {
	SubscriptionCancellationsMonthResponse,
	SubscriptionCancellationsResponse,
	SubscriptionRenewalsMonthResponse,
	SubscriptionRenewalsResponse,
	SubscriptionSignupsMonthResponse,
	SubscriptionSignupsResponse,
	SubscriptionPlanChangesMonthResponse,
	SubscriptionPlanChangesResponse,
	SubscriptionRevenueMonthResponse,
	SubscriptionRevenueResponse,
	SubscriptionSummaryResponse
} from '@interfaces'
import { SUPPRESS_GLOBAL_ERROR_ALERT } from '../interceptors/http-context.tokens'

interface SubscriptionRenewalsQuery {
	from: string
	to: string
	year?: number
	limit?: number
	offset?: number
}

interface SubscriptionRenewalsYearQuery {
	year: number
	limit?: number
	offset?: number
}

interface SubscriptionRenewalsMonthQuery {
	year: number
	month: number
}

@Injectable({
	providedIn: 'root'
})
export class StatsService {
	private http = inject(HttpClient)
	private URL = '/stats'
	private readonly reportContext = new HttpContext().set(SUPPRESS_GLOBAL_ERROR_ALERT, true)

	getSubscriptionRenewals(params: SubscriptionRenewalsYearQuery) {
		let httpParams = new HttpParams().set('year', params.year.toString())
		if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString())
		if (params.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString())

		return this.http.get<SubscriptionRenewalsResponse>(`${this.URL}/subscriptions/renewals`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionCancellations(params: SubscriptionRenewalsQuery) {
		let httpParams = new HttpParams().set('from', params.from).set('to', params.to)
		if (params.year !== undefined) httpParams = httpParams.set('year', params.year.toString())
		if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString())
		if (params.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString())

		return this.http.get<SubscriptionCancellationsResponse>(`${this.URL}/subscriptions/cancellations`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionCancellationsByMonth(params: SubscriptionRenewalsMonthQuery) {
		const httpParams = new HttpParams().set('year', params.year.toString()).set('month', params.month.toString())

		return this.http.get<SubscriptionCancellationsMonthResponse>(`${this.URL}/subscriptions/cancellations/monthly`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionRevenue(params: SubscriptionRenewalsYearQuery) {
		let httpParams = new HttpParams().set('year', params.year.toString())
		if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString())
		if (params.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString())

		return this.http.get<SubscriptionRevenueResponse>(`${this.URL}/subscriptions/revenue`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionRevenueByMonth(params: SubscriptionRenewalsMonthQuery) {
		const httpParams = new HttpParams().set('year', params.year.toString()).set('month', params.month.toString())

		return this.http.get<SubscriptionRevenueMonthResponse>(`${this.URL}/subscriptions/revenue/monthly`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionSignups(params: SubscriptionRenewalsYearQuery) {
		let httpParams = new HttpParams().set('year', params.year.toString())
		if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString())
		if (params.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString())

		return this.http.get<SubscriptionSignupsResponse>(`${this.URL}/subscriptions/signups`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionSignupsByMonth(params: SubscriptionRenewalsMonthQuery) {
		const httpParams = new HttpParams().set('year', params.year.toString()).set('month', params.month.toString())

		return this.http.get<SubscriptionSignupsMonthResponse>(`${this.URL}/subscriptions/signups/monthly`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionPlanChanges(params: SubscriptionRenewalsYearQuery) {
		let httpParams = new HttpParams().set('year', params.year.toString())
		if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString())
		if (params.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString())

		return this.http.get<SubscriptionPlanChangesResponse>(`${this.URL}/subscriptions/plan-changes`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionPlanChangesByMonth(params: SubscriptionRenewalsMonthQuery) {
		const httpParams = new HttpParams().set('year', params.year.toString()).set('month', params.month.toString())

		return this.http.get<SubscriptionPlanChangesMonthResponse>(`${this.URL}/subscriptions/plan-changes/monthly`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionRenewalsByMonth(params: SubscriptionRenewalsMonthQuery) {
		const httpParams = new HttpParams().set('year', params.year.toString()).set('month', params.month.toString())

		return this.http.get<SubscriptionRenewalsMonthResponse>(`${this.URL}/subscriptions/renewals/monthly`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionSummary(params: { year: number }) {
		const httpParams = new HttpParams().set('year', params.year.toString())

		return this.http.get<SubscriptionSummaryResponse>(`${this.URL}/summary`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionAnnualTotals(params: { year: number }) {
		const httpParams = new HttpParams().set('year', params.year.toString())

		return this.http.get<SubscriptionAnnualTotalsResponse>(`${this.URL}/subscriptions/annual-totals`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionReportSnapshot(params: { year: number }) {
		const httpParams = new HttpParams().set('year', params.year.toString())

		return this.http.get<SubscriptionReportSnapshotResponse>(`${this.URL}/subscriptions/report-snapshot`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	getSubscriptionReportIncome(params: { year: number; section: SubscriptionIncomeSection }) {
		const httpParams = new HttpParams().set('year', params.year.toString()).set('section', params.section)

		return this.http.get<SubscriptionReportIncomeResponse>(`${this.URL}/subscriptions/report-snapshot/income`, {
			params: httpParams,
			context: this.reportContext
		})
	}

	requestSubscriptionReportRefresh(params: { year: number }) {
		const httpParams = new HttpParams().set('year', params.year.toString())
		return this.http.post<SubscriptionReportRefreshResponse>(
			`${this.URL}/subscriptions/report-snapshot/refresh`,
			null,
			{ params: httpParams, context: this.reportContext }
		)
	}

	getSubscriptionIncomeBreakdown(params: { year: number; month?: number; scope: 'MONTH' | 'YEAR'; refresh?: boolean }) {
		let httpParams = new HttpParams().set('year', params.year.toString()).set('scope', params.scope)
		if (params.month !== undefined) httpParams = httpParams.set('month', params.month.toString())
		if (params.refresh) httpParams = httpParams.set('refresh', 'true')

		return this.http.get<SubscriptionIncomeBreakdownResponse>(`${this.URL}/subscriptions/income-breakdown`, {
			params: httpParams,
			context: this.reportContext
		})
	}
}

export type SubscriptionIncomeSection = 'renewals' | 'signups' | 'planChanges'

interface SnapshotCountSeries {
	counts: Record<string, number>
	total: number
}

interface SnapshotAmountSeries {
	amounts: Record<string, number>
	total: number
}

export interface SubscriptionReportSnapshotResponse {
	year: number
	status: 'READY' | 'REFRESHING' | 'REFRESH_FAILED'
	dataAsOf: string
	publishedAt: string
	calculationVersion: string
	months: string[]
	summary: {
		activeUsers: number
		activeUsersSource?: SubscriptionSummaryResponse['totals']['activeUsersSource']
		signups: number
		cancellations: number
		revenue: number
	}
	reports: {
		renewals: { monthly: SnapshotCountSeries; annual: SnapshotCountSeries }
		cancellations: { monthly: SnapshotCountSeries; annual: SnapshotCountSeries }
		signups: { monthly: SnapshotCountSeries; annual: SnapshotCountSeries }
		planChanges: {
			monthlyToMonthly: SnapshotCountSeries
			monthlyToAnnual: SnapshotCountSeries
			annualToAnnual: SnapshotCountSeries
			annualToMonthly: SnapshotCountSeries
		}
		revenue: { monthly: SnapshotAmountSeries; annual: SnapshotAmountSeries }
	}
	activeAccounts: {
		withActivePlan: {
			monthlyPayment: { amounts: Record<string, number | null>; total: number | null }
			annualPayment: { amounts: Record<string, number | null>; total: number | null }
		}
		withoutActivePlan: { amounts: Record<string, number | null>; total: number | null }
		qualityByMonth: Record<string, 'OBSERVED' | 'RECONSTRUCTED' | 'CALCULATED'>
	}
}

export interface SubscriptionReportIncomeResponse {
	year: number
	section: SubscriptionIncomeSection
	status: 'READY' | 'REFRESHING' | 'REFRESH_FAILED'
	dataAsOf: string
	calculationVersion: string
	includesForecast: boolean
	months: string[]
	series: {
		monthly: SubscriptionIncomeSeries
		annual: SubscriptionIncomeSeries
	}
}

export interface SubscriptionReportRefreshResponse {
	year: number
	status: 'QUEUED'
	run: {
		run_id: string
		run_status: string
		pending_chunks: number
	}
}

export interface SubscriptionIncomeBreakdownResponse {
	year: number
	scope: 'MONTH' | 'YEAR'
	month?: string
	lastUpdated: string
	incomeBreakdown: {
		renewals: { monthly: number; annual: number }
		signups: { monthly: number; annual: number }
		planChanges: { monthly: number; annual: number }
	}
	referenceNet: { monthly: number; annual: number; total: number }
	reconciliation: {
		components: { monthly: number; annual: number; total: number }
		difference: { monthly: number; annual: number; total: number }
		matches: boolean
		cadenceMatches: boolean
	}
	months: string[]
	incomeSeries: {
		renewals: SubscriptionIncomeSeriesCategory
		signups: SubscriptionIncomeSeriesCategory
		planChanges: SubscriptionIncomeSeriesCategory
	}
	reconciliationSeries: {
		byMonth: Record<string, SubscriptionIncomeBreakdownSnapshot>
		total: SubscriptionIncomeBreakdownSnapshot
	}
}

export interface SubscriptionIncomeSeries {
	amounts: Record<string, number>
	total: number
}

export interface SubscriptionIncomeSeriesCategory {
	monthly: SubscriptionIncomeSeries
	annual: SubscriptionIncomeSeries
}

export interface SubscriptionIncomeBreakdownSnapshot {
	incomeBreakdown: SubscriptionIncomeBreakdownResponse['incomeBreakdown']
	referenceNet: SubscriptionIncomeBreakdownResponse['referenceNet']
	reconciliation: SubscriptionIncomeBreakdownResponse['reconciliation']
}

export interface SubscriptionAnnualTotalsResponse {
	year: number
	lastUpdated: string
	totals: {
		renewals: {
			monthly: number
			annual: number
		}
		cancellations: {
			monthly: number
			annual: number
		}
		signups: {
			monthly: number
			annual: number
		}
		planChanges: {
			monthlyToMonthly: number
			monthlyToAnnual: number
			annualToAnnual: number
			annualToMonthly: number
		}
		revenue: {
			monthly: number
			annual: number
		}
	}
}
